import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { memberApi, useMemberMutation, errorMessage } from "@/hooks/useMember";

const steps = ["Product", "Your answers", "Review"];

const inputCls = "w-full bg-cream border border-forest/20 px-4 py-3 text-sm text-charcoal focus:border-forest outline-none transition-colors";

export default function ApplicationForm({ programme, onDone }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [confirmEligibility, setConfirmEligibility] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);

  const apply = useMemberMutation(memberApi.apply, () => {
    toast.success("Application submitted. You can track it in My Rituals.");
    onDone?.();
  });

  const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const missing = programme.questions.filter((qq) => qq.required && !String(answers[qq.id] || "").trim());

  const next = () => {
    if (step === 1 && missing.length) {
      toast.error(`Please answer: ${missing[0].label}`);
      return;
    }
    setStep((s) => s + 1);
  };

  const submit = () => {
    if (!confirmEligibility || !confirmTerms) {
      toast.error("Please confirm eligibility and the participation terms.");
      return;
    }
    apply.mutate(
      { programme_id: programme.programme_id, answers, confirm_eligibility: true, confirm_terms: true },
      { onError: (e) => toast.error(errorMessage(e)) }
    );
  };

  return (
    <div className="border border-forest/20 p-1" data-testid="application-form">
      <div className="border border-forest/10 p-6 sm:p-8">
        <ol className="flex items-center gap-4 text-[10px] uppercase tracking-[0.22em]">
          {steps.map((s, i) => (
            <li key={s} className={`flex items-center gap-2 ${i === step ? "text-forest" : i < step ? "text-moss" : "text-charcoal/40"}`}>
              <span className={`w-5 h-5 inline-flex items-center justify-center border text-[10px] ${i < step ? "bg-moss border-moss text-ivory" : i === step ? "border-forest" : "border-charcoal/30"}`}>
                {i < step ? <Check size={11} /> : i + 1}
              </span>
              <span className="hidden sm:inline">{s}</span>
              {i < steps.length - 1 && <span className="w-6 h-px bg-forest/15" />}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="mt-8" data-testid="apply-step-product">
            <p className="eyebrow">Step 1</p>
            <h3 className="mt-4 font-serif font-light text-3xl text-forest">Confirm the product</h3>
            <div className="mt-6 flex items-center gap-5 border border-forest/15 bg-cream p-5">
              {programme.product?.image && (
                <div className="w-20 h-24 bg-linen overflow-hidden shrink-0">
                  <img src={programme.product.image} alt="" className="w-full h-full object-contain p-2 mix-blend-multiply" />
                </div>
              )}
              <div>
                <p className="font-serif text-2xl text-forest">{programme.product?.name}</p>
                <p className="text-[11px] uppercase tracking-[0.22em] text-champagne mt-1">{programme.product?.routine_role}</p>
                <p className="mt-2 text-sm text-charcoal/65 font-light">Formulation details will be shared soon. This programme relates to this product only.</p>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-8 space-y-6" data-testid="apply-step-questions">
            <p className="eyebrow">Step 2</p>
            <h3 className="font-serif font-light text-3xl text-forest">A few honest answers</h3>
            {programme.questions.map((qq) => (
              <label key={qq.id} className="block">
                <span className="text-sm text-charcoal/80">
                  {qq.label} {qq.required && <span className="text-champagne">*</span>}
                </span>
                {qq.type === "select" ? (
                  <select value={answers[qq.id] || ""} onChange={(e) => set(qq.id, e.target.value)} data-testid={`apply-q-${qq.id}`} className={`mt-2 ${inputCls}`}>
                    <option value="">Choose…</option>
                    {qq.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <textarea rows={3} value={answers[qq.id] || ""} onChange={(e) => set(qq.id, e.target.value)} data-testid={`apply-q-${qq.id}`} className={`mt-2 ${inputCls}`} />
                )}
              </label>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="mt-8" data-testid="apply-step-review">
            <p className="eyebrow">Step 3</p>
            <h3 className="mt-4 font-serif font-light text-3xl text-forest">Review and confirm</h3>
            <dl className="mt-6 divide-y divide-forest/10 border-y border-forest/10">
              <div className="py-3 flex justify-between gap-6 text-sm"><dt className="text-charcoal/55">Programme</dt><dd className="text-right text-charcoal">{programme.title}</dd></div>
              <div className="py-3 flex justify-between gap-6 text-sm"><dt className="text-charcoal/55">Product</dt><dd className="text-right text-charcoal">{programme.product?.name}</dd></div>
              {programme.questions.map((qq) => (
                <div key={qq.id} className="py-3 flex justify-between gap-6 text-sm">
                  <dt className="text-charcoal/55 max-w-[45%]">{qq.label}</dt>
                  <dd className="text-right text-charcoal">{answers[qq.id] || <span className="italic text-charcoal/40">Not answered</span>}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 space-y-4">
              <label className="flex items-start gap-3 text-sm text-charcoal/80 cursor-pointer">
                <Checkbox checked={confirmEligibility} onCheckedChange={(v) => setConfirmEligibility(!!v)} data-testid="apply-confirm-eligibility" className="mt-0.5" />
                I confirm I meet the eligibility criteria listed for this programme.
              </label>
              <label className="flex items-start gap-3 text-sm text-charcoal/80 cursor-pointer">
                <Checkbox checked={confirmTerms} onCheckedChange={(v) => setConfirmTerms(!!v)} data-testid="apply-confirm-terms" className="mt-0.5" />
                I understand the participation terms, including that applying does not guarantee a product.
              </label>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          {step > 0 ? (
            <button type="button" onClick={() => setStep((s) => s - 1)} className="btn btn-outline" data-testid="apply-back">Back</button>
          ) : <span />}
          {step < 2 ? (
            <button type="button" onClick={next} className="btn btn-primary" data-testid="apply-next">Continue</button>
          ) : (
            <button type="button" onClick={submit} disabled={apply.isPending} className="btn btn-primary disabled:opacity-60" data-testid="apply-submit">
              {apply.isPending ? "Submitting…" : "Submit application"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
